import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUserStore } from "../../../store/user";
import { homeService } from "../services";
import { Sidebar } from "../../../components/sidebar";
import { EditProfileModal, type Profile } from "../../profiles-tracking/components/edit-profile-modal";
import { toast } from "react-toastify";
import { profilesTrackingService } from "../../profiles-tracking/services";
import { useEditProfileModal } from "../../../contexts/useEditProfileModal";
import { PaymentSuccessModal } from "../components/payment-success-modal";
import {
  HomeContainer,
  MainContent,
  TopBar,
  TopBarLeft,
  TopBarRight,
  UserInfoSection,
  UserAvatarLarge,
  UserDetails,
  UserName,
  UserEmail,
  DashboardGrid,
  HeroCard,
  HeroContent,
  HeroTitle,
  HeroSubtitle,
  HeroMetrics,
  MetricItem,
  MetricValue,
  MetricLabel,
  MetricIcon,
  HeroActions,
  PrimaryButton,
  SecondaryButton,
  QuickStatsRow,
  QuickStatCard,
  StatNumber,
  StatText,
  MainSection,
  TimelineActivitySection,
  SectionHeader,
  SectionTitle,
  SectionAction,
  CardsGrid,
  FeatureCard,
  FeatureIcon,
  FeatureTitle,
  FeatureDescription,
  FeatureLink,
  TimelineCard,
  TimelineHeader,
  TimelineTitle,
  TimelineList,
  TimelineItem,
  TimelineDot,
  TimelineContent,
  TimelineText,
  TimelineDate,
  ActivityCard,
  ActivityHeader,
  ActivityTitle,
  ActivityGrid,
  ActivityItem,
  ActivityIcon,
  ActivityText,
  SupportCard,
  SupportIcon,
  SupportContent,
  SupportTitle,
  SupportText,
  SupportActions,
  SupportButton,
  FloatingWhatsAppButton,
} from "./styles";

interface Activity {
  id: string;
  type: "success" | "info" | "warning" | "error";
  title: string;
  time: string;
}

interface DashboardData {
  profileName?: string;
  primaryCategory?: string;
  cityState?: string;
  analytics?: {
    websiteClicks?: number;
    callClicks?: number;
    requestedRoutes?: number;
    viewsInMap?: number;
  };
}

export const Home: React.FC = () => {
  const { user } = useUserStore();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isOpen: isEditModalOpen, closeModal, openModal } = useEditProfileModal();
  const [stats, setStats] = useState({
    profiles: 0,
    active: 0,
    pending: 0,
    total: 0,
  });
  const [activities, setActivities] = useState<Activity[]>([]);
  const [profileData, setProfileData] = useState<Profile | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const userName = user?.name?.split(" ")[0] || "Usuário";
  const userInitial = userName.charAt(0).toUpperCase();
  const currentHour = new Date().getHours();
  const greeting =
    currentHour < 12
      ? "Bom dia"
      : currentHour < 18
      ? "Boa tarde"
      : "Boa noite";

  useEffect(() => {
    const paymentParam = searchParams.get("payment");
    if (paymentParam === "success") {
      setShowPaymentModal(true);
      searchParams.delete("payment");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsData, activitiesData] = await Promise.all([
          homeService.getDashboardStats(),
          homeService.getActivities(),
        ]);

        setStats(statsData);
        setActivities(activitiesData);

        if (user && user.businessProfiles?.[0]) {
          const profileId = user.businessProfiles[0];
          const dashData = await homeService.getProfileDashboard(profileId);

          if (dashData) {
            setDashboardData(dashData);
          }

          const profileDetails = await homeService.getProfileDetails(profileId);

          if (profileDetails) {
            setProfileData(profileDetails as Profile);
          } else {
            const userProfile: Profile = {
              id: profileId,
              name: user.name || "",
              category: "Cliente",
              status: "active" as const,
              stats: {
                views: 0,
                clicks: 0,
                ranking: 0,
              },
            };
            setProfileData(userProfile);
          }
        }
      } catch (error) {
        console.error("Erro ao carregar dados:", error);
      }
    };

    fetchData();
  }, [user]);

  const handleSaveProfile = async (updatedProfile: Profile) => {
    try {
      await profilesTrackingService.updateProfile(
        updatedProfile.id,
        updatedProfile,
      );
      setProfileData(updatedProfile);
      toast.success("Perfil atualizado com sucesso!");
      closeModal();
    } catch (error) {
      console.error("Erro ao salvar perfil:", error);
      toast.error("Erro ao atualizar perfil");
      throw error;
    }
  };

  const handleWhatsApp = () => {
    const numeroWhatsApp = "+558008001700";
    const mensagem = "Olá! Gostaria de falar com a equipe do GeoRanking.";
    const mensagemCodificada = encodeURIComponent(mensagem);
    window.open(`https://wa.me/${numeroWhatsApp}?text=${mensagemCodificada}`, "_blank");
  };

  const handlePhoneCall = () => {
    window.location.href = "tel:+558008001700";
  };

  return (
    <HomeContainer>
      <Sidebar />
      <MainContent>
        {/* Top Bar */}
        <TopBar>
          <TopBarLeft>
            {/* Espaço vazio - pode adicionar logo ou título aqui */}
          </TopBarLeft>
          <TopBarRight>
            <UserInfoSection>
              <UserAvatarLarge>{userInitial}</UserAvatarLarge>
              <UserDetails>
                <UserName>{userName}</UserName>
                <UserEmail>{user?.userType || "Cliente"}</UserEmail>
              </UserDetails>
            </UserInfoSection>
          </TopBarRight>
        </TopBar>

        {/* Dashboard Content */}
        <DashboardGrid>
          {/* Hero Card */}
          <HeroCard>
            <HeroContent>
              <HeroTitle>{greeting}, {userName}! 👋</HeroTitle>
              <HeroSubtitle>
                {dashboardData?.profileName
                  ? `Perfil: ${dashboardData.profileName}${dashboardData.primaryCategory ? ` — ${dashboardData.primaryCategory}` : ""}${dashboardData.cityState ? ` • ${dashboardData.cityState}` : ""}`
                  : "Seu perfil está sendo otimizado com tecnologia certificada e acesso direto à API Google Places."}
              </HeroSubtitle>

              <HeroMetrics>
                <MetricItem>
                  <MetricIcon>
                    <span className="material-icons">visibility</span>
                  </MetricIcon>
                  <div>
                    <MetricValue>{dashboardData?.analytics?.viewsInMap ?? stats.profiles ?? 0}</MetricValue>
                    <MetricLabel>Visualizações no Mapa</MetricLabel>
                  </div>
                </MetricItem>
                <MetricItem>
                  <MetricIcon>
                    <span className="material-icons">ads_click</span>
                  </MetricIcon>
                  <div>
                    <MetricValue>{dashboardData?.analytics?.websiteClicks ?? 0}</MetricValue>
                    <MetricLabel>Cliques no Site</MetricLabel>
                  </div>
                </MetricItem>
                <MetricItem>
                  <MetricIcon>
                    <span className="material-icons">call</span>
                  </MetricIcon>
                  <div>
                    <MetricValue>{dashboardData?.analytics?.callClicks ?? 0}</MetricValue>
                    <MetricLabel>Ligações</MetricLabel>
                  </div>
                </MetricItem>
                <MetricItem>
                  <MetricIcon>
                    <span className="material-icons">directions</span>
                  </MetricIcon>
                  <div>
                    <MetricValue>{dashboardData?.analytics?.requestedRoutes ?? 0}</MetricValue>
                    <MetricLabel>Rotas Solicitadas</MetricLabel>
                  </div>
                </MetricItem>
              </HeroMetrics>

              <HeroActions>
                <PrimaryButton onClick={() => navigate("/dashboard")}>
                  <span className="material-icons">monitoring</span>
                  Ver desempenho
                </PrimaryButton>
                <SecondaryButton onClick={() => navigate("/georanking")}>
                  <span className="material-icons">add_circle</span>
                  Novo Perfil
                </SecondaryButton>
              </HeroActions>
            </HeroContent>
          </HeroCard>

          {/* Quick Stats */}
          <QuickStatsRow>
            <QuickStatCard onClick={() => navigate("/dashboard")}>
              <StatNumber>{stats.profiles || 0}</StatNumber>
              <StatText>Perfis cadastrados</StatText>
            </QuickStatCard>

            <QuickStatCard>
              <StatNumber>{stats.active || 0}</StatNumber>
              <StatText>Perfis ativos</StatText>
            </QuickStatCard>

            <QuickStatCard>
              <StatNumber>{stats.total || 0}</StatNumber>
              <StatText>Total de impulsionamentos</StatText>
            </QuickStatCard>
          </QuickStatsRow>

          {/* Features Section */}
          <MainSection>
            <SectionHeader>
              <SectionTitle>Recursos Disponíveis</SectionTitle>
              <SectionAction onClick={() => navigate("/dashboard")}>
                Ver todos
                <span className="material-icons">arrow_forward</span>
              </SectionAction>
            </SectionHeader>

            <CardsGrid>
              <FeatureCard onClick={() => navigate("/dashboard")}>
                <FeatureIcon $color="#1264C7">
                  <span className="material-icons">analytics</span>
                </FeatureIcon>
                <FeatureTitle>Análise Avançada</FeatureTitle>
                <FeatureDescription>
                  Acompanhe métricas detalhadas e insights sobre o desempenho dos seus perfis no Google Maps.
                </FeatureDescription>
                <FeatureLink href="#" onClick={(e) => { e.preventDefault(); navigate("/dashboard"); }}>
                  Acessar
                  <span className="material-icons">arrow_forward</span>
                </FeatureLink>
              </FeatureCard>

              <FeatureCard onClick={() => openModal()}>
                <FeatureIcon $color="#1E8E3E">
                  <span className="material-icons">location_on</span>
                </FeatureIcon>
                <FeatureTitle>Meus perfis</FeatureTitle>
                <FeatureDescription>
                  Edite as informações da sua ficha no Google: contato, horários, descrição, serviços e postagens.
                </FeatureDescription>
                <FeatureLink href="#" onClick={(e) => { e.preventDefault(); openModal(); }}>
                  Acessar
                  <span className="material-icons">arrow_forward</span>
                </FeatureLink>
              </FeatureCard>

              <FeatureCard onClick={() => navigate("/georanking")}>
                <FeatureIcon $color="#F9AB00">
                  <span className="material-icons">rocket_launch</span>
                </FeatureIcon>
                <FeatureTitle>Impulsionar Perfil</FeatureTitle>
                <FeatureDescription>
                  Crie novos impulsionamentos e apareça nas primeiras posições do Google Maps para seus clientes.
                </FeatureDescription>
                <FeatureLink href="#" onClick={(e) => { e.preventDefault(); navigate("/georanking"); }}>
                  Começar
                  <span className="material-icons">arrow_forward</span>
                </FeatureLink>
              </FeatureCard>
            </CardsGrid>
          </MainSection>

          {/* Timeline & Activity */}
          <TimelineActivitySection>
            <TimelineCard>
              <TimelineHeader>
                <TimelineTitle>Progresso do Setup</TimelineTitle>
              </TimelineHeader>
              <TimelineList>
                <TimelineItem>
                  <TimelineDot $color="#1E8E3E">
                    <span className="material-icons">check</span>
                  </TimelineDot>
                  <TimelineContent>
                    <TimelineText>Cadastro Confirmado</TimelineText>
                    <TimelineDate>Concluído</TimelineDate>
                  </TimelineContent>
                </TimelineItem>

                <TimelineItem>
                  <TimelineDot $color="#1264C7">
                    <span className="material-icons">sync</span>
                  </TimelineDot>
                  <TimelineContent>
                    <TimelineText>Análise Inicial</TimelineText>
                    <TimelineDate>Em andamento...</TimelineDate>
                  </TimelineContent>
                </TimelineItem>

                <TimelineItem>
                  <TimelineDot $color="#E2E6EA">
                    <span className="material-icons">schedule</span>
                  </TimelineDot>
                  <TimelineContent>
                    <TimelineText>Sincronização Google</TimelineText>
                    <TimelineDate>Aguardando (até 24h)</TimelineDate>
                  </TimelineContent>
                </TimelineItem>

                <TimelineItem>
                  <TimelineDot $color="#E2E6EA">
                    <span className="material-icons">assessment</span>
                  </TimelineDot>
                  <TimelineContent>
                    <TimelineText>Relatório Detalhado</TimelineText>
                    <TimelineDate>Aguardando (até 72h)</TimelineDate>
                  </TimelineContent>
                </TimelineItem>
              </TimelineList>
            </TimelineCard>

            <ActivityCard>
              <ActivityHeader>
                <ActivityTitle>Atividades Recentes</ActivityTitle>
              </ActivityHeader>
              <ActivityGrid>
                {activities.length > 0 ? (
                  activities.map((activity) => (
                    <ActivityItem key={activity.id}>
                      <ActivityIcon $color={
                        activity.type === "success" ? "#1E8E3E" :
                        activity.type === "info" ? "#1264C7" :
                        activity.type === "warning" ? "#F9AB00" : "#D93025"
                      }>
                        <span className="material-icons">
                          {activity.type === "success" ? "check_circle" :
                           activity.type === "info" ? "info" :
                           activity.type === "warning" ? "warning" : "error"}
                        </span>
                      </ActivityIcon>
                      <ActivityText>
                        <p>{activity.title}</p>
                        <span>{activity.time}</span>
                      </ActivityText>
                    </ActivityItem>
                  ))
                ) : (
                  <div style={{ gridColumn: "1 / -1", textAlign: "center", padding: "40px 20px" }}>
                    <span className="material-icons" style={{ fontSize: "48px", color: "#E2E6EA", marginBottom: "16px" }}>
                      inbox
                    </span>
                    <p style={{ color: "#5f6368", margin: 0 }}>Nenhuma atividade recente</p>
                  </div>
                )}
              </ActivityGrid>
            </ActivityCard>
          </TimelineActivitySection>

          {/* Support Card */}
          <SupportCard>
            <SupportIcon>
              <span className="material-icons">support_agent</span>
            </SupportIcon>
            <SupportContent>
              <SupportTitle>Precisa de ajuda?</SupportTitle>
              <SupportText>
                Nossa equipe está pronta para atender você! Entre em contato via WhatsApp ou ligue diretamente.
              </SupportText>
              <div style={{ marginTop: "12px", fontSize: "14px", color: "#ffffff" }}>
                <p style={{ margin: "0 0 4px 0" }}><strong>0800 800 1700</strong></p>
              </div>
            </SupportContent>
            <SupportActions>
              <SupportButton $variant="primary" onClick={handleWhatsApp}>
                <svg 
                  viewBox="0 0 24 24" 
                  width="20" 
                  height="20" 
                  fill="currentColor"
                  style={{ marginRight: '8px' }}
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                WhatsApp
              </SupportButton>
              <SupportButton onClick={handlePhoneCall}>
                <span className="material-icons">phone</span>
                Ligar
              </SupportButton>
            </SupportActions>
          </SupportCard>
        </DashboardGrid>
      </MainContent>

      {/* Modal de Edição de Perfil (sem aba IA) */}
      <EditProfileModal
        isOpen={isEditModalOpen}
        profile={profileData}
        onClose={closeModal}
        onSave={handleSaveProfile}
        hideIATab={true}
      />

      {/* Botão Flutuante WhatsApp */}
      <FloatingWhatsAppButton onClick={handleWhatsApp} title="Entrar em contato via WhatsApp">
        <svg 
          viewBox="0 0 24 24" 
          width="28" 
          height="28" 
          fill="currentColor"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
        </svg>
      </FloatingWhatsAppButton>

      {/* Modal de Pagamento Confirmado */}
      <PaymentSuccessModal
        open={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
      />
    </HomeContainer>
  );
};
